/**
 * Подстановка переменных в шаблон подписи — общая для названий указателей
 * (`wayfindingSignNames.ts`) и подписей объектов (`wayfindingObjectFields.ts`). Синтаксис
 * переменной у каждого свой (регулярка), а правило пустоты одно: шаблон, все переменные
 * которого пусты, ничего не называет.
 */

export interface RenderedTemplate {
  text: string;
  /** В шаблоне была хоть одна знакомая переменная. */
  hadVariables: boolean;
  /** Хоть одна переменная дала непустое значение. */
  filled: boolean;
}

/**
 * Потолок длины подписи. Шаблон и значения лежат в свободном jsonb карты и в свойствах объектов, а
 * переменную можно повторить: «{projectIndex}» × тысячи при длинном индексе строили гигабайты на
 * каждый указатель, а на пределе тела запроса — RangeError, и редактор карты не открывался.
 * Настоящие названия и подписи — десятки знаков; длиннее подпись обрезается.
 */
export const TEMPLATE_LABEL_MAX_LENGTH = 1000;

/**
 * Сколько шаблона просматривать. Больше потолка подписи: переменные могут оказаться пустыми, и
 * заполненная после них подпись иначе пропала бы. Не весь шаблон: он ограничен только телом
 * запроса, а считается на каждый указатель карты.
 */
const TEMPLATE_SCAN_MAX_LENGTH = 16 * TEMPLATE_LABEL_MAX_LENGTH;

/**
 * @param token глобальная регулярка переменной, без именованных групп.
 * @param resolve значение переменной по группам совпадения; `null` — переменная незнакома и
 *   остаётся как написана (опечатка видна в подписи, а не пропадает молча).
 */
export function renderTemplateTokens(
  template: string,
  token: RegExp,
  resolve: (groups: ReadonlyArray<string | undefined>) => string | null,
): RenderedTemplate {
  let hadVariables = false;
  let filled = false;
  // Значениям — не больше потолка на всю подпись: результат без последней обрезки не длиннее
  // просмотренного шаблона и одного потолка.
  let budget = TEMPLATE_LABEL_MAX_LENGTH;
  const source = template.slice(0, TEMPLATE_SCAN_MAX_LENGTH);
  const text = source.replace(token, (...args: unknown[]) => {
    // Бюджет кончился, значит, непустое значение уже было, и подпись дальше всё равно обрежется.
    if (budget === 0) {
      return '';
    }
    // Аргументы колбэка: совпадение, группы, смещение, исходная строка.
    const value = resolve(args.slice(1, -2) as Array<string | undefined>);
    if (value === null) {
      return args[0] as string;
    }
    hadVariables = true;
    if (value !== '') {
      filled = true;
    }
    const shown = value.length > budget ? value.slice(0, budget) : value;
    budget -= shown.length;
    return shown;
  });
  return { text: text.slice(0, TEMPLATE_LABEL_MAX_LENGTH), hadVariables, filled };
}

/**
 * Подпись из подставленного шаблона: `''`, если все переменные пусты — строка из одних
 * разделителей ничего не называет, и вызывающий покажет своё. Шаблон без переменных вовсе —
 * осознанный выбор человека, его текст возвращается как есть.
 */
export function renderedTemplateLabel({ text, hadVariables, filled }: RenderedTemplate): string {
  return hadVariables && !filled ? '' : text.trim();
}
